import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Order } from '../types';
import { OrderCreateEntry } from 'public-contracts';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';

import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft } from 'lucide-react';
import { useUserStore } from '../stores/useUserStore';
import { ordersService } from '../services/ordersService';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';

// Import existing components that we'll reuse
import { ModernCustomerSelector } from './components/ModernCustomerSelector';
import { ModernItemsGrid } from './components/ModernItemsGrid';
import { OrderTotalField } from './components/OrderTotalField';
import { PointsRedemption } from './components/PointsRedemption';
import { ModernTableSelector } from './components/ModernTableSelector';
import { PrintBillButton } from './components/PrintBillButton';
import { CancelOrderButton } from './components/CancelOrderButton';
import { PromotionSelector } from './components/PromotionSelector';
import { PromotionStatusBadge } from './components/PromotionStatusBadge';
import { useTableStatusStore } from '../stores/useTableStatusStore';
import { promotionsService } from '../services/promotionsService';
import { PromotionDiscountType, PromotionStatus } from 'public-contracts';
import CustomerReferenceField from '../visitors/CustomerReferenceField';
import { Plus, ExternalLink, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import { authenticatedFetch } from '../utils/authenticatedFetch';

// Helper function to format date for datetime-local input (in local timezone, not UTC)
const formatDateForDateTimeLocal = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  // Format in local timezone: YYYY-MM-DDTHH:mm
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Form data interface extending OrderCreateEntry
interface OrderFormData
  extends Omit<
    OrderCreateEntry,
    | 'id'
    | 'paid'
    | 'pointsEarned'
    | 'taxes'
    | 'taxRate'
    | 'total'
    | 'totalExTaxes'
    | 'deliveryFees'
    | 'tenantId'
  > {
  promotionIds: string[]; // Array of selected promotion IDs
  promotionType?: 'none' | 'percentage' | 'fixed'; // Manual promotion type
  promotionValue?: number; // Manual promotion value
  customerPoints: number | null;
  pointsRedemption: number;
  orderType: 'dine_in' | 'takeaway' | 'delivery';
  tableNumber?: number;
  items: Array<{
    productId: string;
    quantity: number;
    price: number;
    note?: string;
  }>;
  date: Date;
  customerId: string;
  status: 'ordered' | 'ready' | 'delivered' | 'cancelled';
  hasCheckedIn: boolean;
  tempCustomerId?: string;
}

const PromotionSection = ({ disabled = false }: { disabled?: boolean }) => {
  const { translate } = useTranslation();
  const { watch, setValue } = useFormContext<OrderFormData>();
  const promotionIds = watch('promotionIds') || [];
  const promotionType = watch('promotionType') || 'none';
  const promotionValue = watch('promotionValue') || 0;
  const orderType = watch('orderType');
  const items = watch('items') || [];
  const customerPoints = watch('customerPoints');

  const totalAmount = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handlePromotionChange = (newPromotionIds: string[]) => {
    setValue('promotionIds', newPromotionIds, { shouldDirty: true });
    // Clear manual promotion when selecting from list
    if (newPromotionIds.length > 0) {
      setValue('promotionType', 'none', { shouldDirty: true });
      setValue('promotionValue', 0, { shouldDirty: true });
    }
  };

  const handleManualPromotionChange = () => {
    // Clear promotionIds when using manual promotion
    if (promotionType !== 'none' && promotionValue > 0) {
      setValue('promotionIds', [], { shouldDirty: true });
    }
  };

  const hasSelectedPromotions = promotionIds.length > 0;
  const hasManualPromotion = promotionType !== 'none' && promotionValue > 0;
  const showPointsRedemption = !hasSelectedPromotions && !hasManualPromotion;

  return (
    <div className="space-y-4">
      <PromotionSelector
        selectedPromotionIds={promotionIds}
        onChange={handlePromotionChange}
        orderType={orderType}
        disabled={disabled}
      />

      {/* Manual Promotion Input - Show when no promotions selected */}
      {!hasSelectedPromotions && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {translate(
                'pos.orders.fields.manualPromotion',
                'Manual Discount'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                {translate('pos.orders.fields.promotionType')}
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={promotionType}
                onChange={(e) => {
                  const newType = e.target.value as
                    | 'none'
                    | 'percentage'
                    | 'fixed';
                  setValue('promotionType', newType, { shouldDirty: true });
                  // Always reset promotion value when type changes
                  // This ensures old values don't persist when switching between types
                  setValue('promotionValue', 0, { shouldDirty: true });
                  handleManualPromotionChange();
                }}
                disabled={disabled}
                aria-label={translate(
                  'pos.orders.fields.promotionType'
                )}
              >
                <option value="none">
                  {translate('pos.orders.promotion.none')}
                </option>
                <option value="percentage">
                  {translate('pos.orders.promotion.percentage')}
                </option>
                <option value="fixed">
                  {translate('pos.orders.promotion.fixed')}
                </option>
              </select>
            </div>

            {promotionType !== 'none' && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {translate('pos.orders.fields.promotionValue')}
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                  min={0}
                  max={promotionType === 'percentage' ? 100 : totalAmount}
                  value={promotionValue}
                  onChange={(e) => {
                    setValue('promotionValue', Number(e.target.value), {
                      shouldDirty: true,
                    });
                    handleManualPromotionChange();
                  }}
                  disabled={disabled}
                />
                {promotionType === 'percentage' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {translate(
                      'pos.orders.promotion.percentageHint',
                      'Enter percentage (0-100)'
                    )}
                  </p>
                )}
                {promotionType === 'fixed' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {translate(
                      'pos.orders.promotion.fixedHint',
                      `Enter fixed amount (max: ${totalAmount})`
                    )}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showPointsRedemption && (
        <PointsRedemption
          customerPoints={Number(customerPoints || 0)}
          disabled={disabled}
          totalAmount={totalAmount}
        />
      )}
    </div>
  );
};

const ReferenceDisplay = ({ reference }: { reference: string }) => {
  const { translate } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {translate('pos.orders.fields.reference')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-lg font-mono bg-gray-100 p-3 rounded-md">
          {reference}
        </p>
      </CardContent>
    </Card>
  );
};

const OrderForm = ({
  order,
  onOrderUpdate,
}: {
  order: Order;
  onOrderUpdate?: (order: Order) => void;
}) => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();
  const { formatCurrency } = useCurrencyFormat();
  const isStaff = user?.roles?.includes('staff');
  const [currentOrder, setCurrentOrder] = React.useState<Order>(order);
  const [suborders, setSuborders] = React.useState<Order[]>([]);
  const [loadingSuborders, setLoadingSuborders] = React.useState(false);
  const [expandedSuborders, setExpandedSuborders] = React.useState<Set<string>>(new Set());
  const { fetchTableStatuses } = useTableStatusStore();
  
  // Product cache for suborder items
  const productCache = React.useRef<Map<string | number, any>>(new Map());
  
  // Check if this is a suborder
  const isSuborder = Boolean(currentOrder.parentOrderId);
  // Track original items count to prevent editing existing items in suborders
  const [originalItemsCount, setOriginalItemsCount] = React.useState(
    currentOrder.items?.length || 0
  );

  const form = useForm<OrderFormData>({
    defaultValues: {
      reference: currentOrder.reference,
      promotionIds: currentOrder.promotionIds || [],
      customerPoints: null,
      pointsRedemption: currentOrder.pointsRedemption || 0,
      orderType:
        (currentOrder.orderType as 'dine_in' | 'takeaway' | 'delivery') ||
        undefined,
      tableNumber:
        typeof currentOrder.tableNumber === 'number'
          ? currentOrder.tableNumber
          : currentOrder.tableNumber
          ? Number(currentOrder.tableNumber)
          : undefined,
      items: (currentOrder.items || []).map((item) => ({
        productId: String(item.productId),
        quantity: item.quantity,
        price: item.price,
        note: item.note,
      })),
      date: new Date(currentOrder.date),
      status: currentOrder.status,
      customerId: String(currentOrder.customerId || ''),
      hasCheckedIn: false,
      tempCustomerId: currentOrder.customerId
        ? String(currentOrder.customerId)
        : undefined,
    },
  });

  // Fetch suborders if this is a parent order (regardless of status)
  React.useEffect(() => {
    const fetchSuborders = async () => {
      if (!currentOrder.parentOrderId) {
        // This is a parent order, fetch its suborders
        setLoadingSuborders(true);
        try {
          // Fetch orders with pagination to ensure we get all suborders
          // Paginate through all pages to find all suborders
          let orderSuborders: Order[] = [];
          let page = 1;
          const perPage = 1000;
          let hasMore = true;

          while (hasMore) {
            const ordersResponse = await ordersService.getOrders({
              pagination: { page, perPage },
              sort: { field: 'date', order: 'DESC' },
              filter: {},
            });

            const pageSuborders = (ordersResponse.data || []).filter(
              (o: Order) => o.parentOrderId === currentOrder.id
            );
            orderSuborders = orderSuborders.concat(pageSuborders);

            // Check if we've fetched all orders
            if (
              ordersResponse.data.length < perPage ||
              orderSuborders.length >= (ordersResponse.total || 0)
            ) {
              hasMore = false;
            } else {
              page++;
            }
          }

          console.log(`Found ${orderSuborders.length} suborders for order ${currentOrder.id}`);
          setSuborders(orderSuborders);
        } catch (error) {
          console.error('Error fetching suborders:', error);
          setSuborders([]);
        } finally {
          setLoadingSuborders(false);
        }
      } else {
        setSuborders([]);
      }
    };
    fetchSuborders();
  }, [currentOrder.id, currentOrder.parentOrderId]);

  // Load promotion details if order has temporary promotions
  React.useEffect(() => {
    const loadPromotionDetails = async () => {
      if (
        !currentOrder.promotionIds ||
        currentOrder.promotionIds.length === 0
      ) {
        return;
      }

      try {
        // Fetch all promotion details
        const promotionDetails = await Promise.all(
          currentOrder.promotionIds.map((id) =>
            promotionsService.getPromotion(id).catch(() => null)
          )
        );

        // Check if any promotion is a temporary one (manual discount)
        const temporaryPromotion = promotionDetails.find(
          (promo) =>
            promo &&
            promo.name?.startsWith('Temporary Discount') &&
            (promo.status === PromotionStatus.PENDING_APPROVAL ||
              promo.status === PromotionStatus.ACTIVE)
        );

        if (temporaryPromotion) {
          // Extract promotion type and value from temporary promotion
          const promotionType =
            temporaryPromotion.discountType === PromotionDiscountType.PERCENTAGE
              ? 'percentage'
              : temporaryPromotion.discountType ===
                PromotionDiscountType.FIXED_AMOUNT
              ? 'fixed'
              : 'none';
          const promotionValue = temporaryPromotion.discountValue || 0;

          // Update form with extracted values and clear promotionIds to show manual promotion section
          form.setValue(
            'promotionType',
            promotionType as 'none' | 'percentage' | 'fixed',
            {
              shouldDirty: false,
            }
          );
          form.setValue('promotionValue', promotionValue, {
            shouldDirty: false,
          });
          form.setValue('promotionIds', [], { shouldDirty: false });
        }
      } catch (error) {
        console.error('Error loading promotion details:', error);
      }
    };

    loadPromotionDetails();
  }, [currentOrder.id, currentOrder.promotionIds, form]);

  // Update form when order ID changes (not when individual fields change)
  // This prevents resetting the form and clearing dirty state when user makes changes
  React.useEffect(() => {
    form.reset(
      {
        reference: currentOrder.reference,
        promotionIds: currentOrder.promotionIds || [],
        promotionType: 'none', // Will be updated by loadPromotionDetails if temporary promotion exists
        promotionValue: 0, // Will be updated by loadPromotionDetails if temporary promotion exists
        customerPoints: null,
        pointsRedemption: currentOrder.pointsRedemption || 0,
        orderType:
          (currentOrder.orderType as 'dine_in' | 'takeaway' | 'delivery') ||
          undefined,
        tableNumber:
          typeof currentOrder.tableNumber === 'number'
            ? currentOrder.tableNumber
            : currentOrder.tableNumber
            ? Number(currentOrder.tableNumber)
            : undefined,
        items: (currentOrder.items || []).map((item) => ({
          productId: String(item.productId),
          quantity: item.quantity,
          price: item.price,
          note: item.note,
        })),
        date: new Date(currentOrder.date),
        status: currentOrder.status,
        customerId: String(currentOrder.customerId || ''),
        hasCheckedIn: false,
        tempCustomerId: currentOrder.customerId
          ? String(currentOrder.customerId)
          : undefined,
      },
      { keepDirty: false }
    ); // Only reset when order ID changes, allow dirty state to persist
  }, [
    currentOrder.id, // Only reset when order ID changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    form, // form is stable, but included for completeness
  ]);

  const {
    watch,
    formState: { isDirty, dirtyFields },
  } = form;

  // Debug: Log dirty state changes
  React.useEffect(() => {
    console.log('[OrderEdit] Form dirty state:', { isDirty, dirtyFields });
  }, [isDirty, dirtyFields]);
  const orderType = watch('orderType');
  const tableNumber = watch('tableNumber');
  const items = watch('items') || [];
  const tempCustomerId = watch('tempCustomerId');
  const hasCheckedIn = watch('hasCheckedIn');

  const isDineIn = orderType === 'dine_in';
  const hasTable = Boolean(tableNumber);
  const hasItems = items.length > 0;
  const hasSelectedCustomer = Boolean(tempCustomerId);
  // For orders in "ordered" state, allow customer changes without check-in
  // Check-in is only required for membership benefits (points redemption, etc.)
  // For editing orders, customer selection is optional and doesn't require check-in
  const isCustomerValid = true; // Customer selection is optional, check-in not required for basic edits

  // Check if order is a delivery order from a platform (Grab, Shopee, Xanh SM)
  // These orders can be imported 1-2 weeks after actual day and need to be editable
  const isPlatformDeliveryOrder =
    currentOrder.orderType === 'delivery' &&
    currentOrder.sourcePlatform &&
    currentOrder.sourcePlatform !== 'internal';

  const canEditPoints =
    !currentOrder.paid &&
    currentOrder.customerId &&
    currentOrder.status === 'ordered' &&
    !isSuborder; // Suborders cannot edit points
  const canEditOrder = !currentOrder.paid && currentOrder.status === 'ordered' && !isSuborder; // Suborders cannot edit order fields
  // For suborders, allow adding items regardless of status (but not editing existing items)
  const canAddItemsToSuborder = isSuborder && !currentOrder.paid;
  // Allow promotion editing for orders in "ordered" status only (not ready, delivered, or cancelled)
  // This allows modifying promotions even without a customer, as long as order is in "ordered" status and not paid
  // For platform delivery orders, allow editing promotions regardless of status (can be imported later)
  // Suborders cannot edit promotions
  const canEditPromotion = (canEditOrder || isPlatformDeliveryOrder) && !isSuborder;

  // Allow editing date and all fields for platform delivery orders
  // These orders can be verified on other platforms and imported later
  // Suborders cannot edit date
  const canEditDate = (isPlatformDeliveryOrder || canEditOrder) && !isSuborder;

  // Allow customer tagging for finished orders that don't have a customer
  // But prevent changing customer if order already has one and is finished
  const isFinished =
    currentOrder.status === 'delivered' || currentOrder.status === 'cancelled';
  const hasCustomer = Boolean(currentOrder.customerId);
  const canTagCustomer = isFinished && !hasCustomer && !isSuborder; // Can tag if finished and no customer, but not for suborders
  // For platform delivery orders, allow changing customer even if finished (for verification)
  // Suborders cannot change customer
  const canChangeCustomer =
    (canEditOrder || canTagCustomer || isPlatformDeliveryOrder) && !isSuborder; // Can change if editable OR can tag OR platform delivery order, but not for suborders

  const isValidForm =
    hasItems && (!isDineIn || (isDineIn && hasTable)) && isCustomerValid;
  // Only allow saving if order can be edited (ordered status, not paid) and form is valid and dirty
  // For platform delivery orders, allow saving even if status is not 'ordered' (can be imported later)
  // For suborders, allow saving if new items were added (regardless of status)
  const canSavePlatformOrder =
    isPlatformDeliveryOrder && isDirty && isValidForm;
  const canSaveSuborder = canAddItemsToSuborder && isDirty && isValidForm;
  const canSave =
    (canEditOrder || canSavePlatformOrder || canSaveSuborder) && isDirty && isValidForm;
  const canSaveCustomerTag = canTagCustomer && Boolean(tempCustomerId);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ordered':
        return 'bg-blue-100 text-blue-800';
      case 'ready':
        return 'bg-yellow-100 text-yellow-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Component to display product name for suborder items
  const SuborderProductName = ({ productId }: { productId: string | number }) => {
    const [loading, setLoading] = React.useState(!productCache.current.has(productId));
    const [product, setProduct] = React.useState(productCache.current.get(productId));

    React.useEffect(() => {
      if (!productId) return;

      if (productCache.current.has(productId)) {
        setProduct(productCache.current.get(productId));
        setLoading(false);
        return;
      }

      setLoading(true);
      authenticatedFetch(`/api/products/${productId}`)
        .then((response) => response.json())
        .then((productData) => {
          productCache.current.set(productId, productData);
          setProduct(productData);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error loading product:', err);
          setLoading(false);
        });
    }, [productId]);

    if (loading) {
      return <span className="text-sm text-gray-400 animate-pulse">Loading...</span>;
    }

    if (!product) {
      return <span className="text-sm text-gray-500">{productId}</span>;
    }

    return (
      <div>
        <p className="text-sm font-medium">{product.name}</p>
        {product.reference && (
          <p className="text-xs text-gray-500">{product.reference}</p>
        )}
      </div>
    );
  };

  const toggleSuborderExpansion = (suborderId: string) => {
    setExpandedSuborders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(suborderId)) {
        newSet.delete(suborderId);
      } else {
        newSet.add(suborderId);
      }
      return newSet;
    });
  };

  const handleDeleteSuborder = async (suborderId: string) => {
    try {
      const suborder = suborders.find((s) => s.id === suborderId);
      if (!suborder) return;

      // Check if suborder has items
      if ((suborder.items || []).length > 0) {
        notify(
          translate(
            'pos.orders.messages.cannotDeleteSuborderWithItems',
            'Cannot delete suborder with items. Please remove all items first.'
          ),
          { type: 'error' }
        );
        return;
      }

      // Confirm deletion
      if (
        !window.confirm(
          translate(
            'pos.orders.messages.confirmDeleteSuborder',
            'Are you sure you want to delete this suborder? This action cannot be undone.'
          )
        )
      ) {
        return;
      }

      // Delete the suborder
      await ordersService.deleteOrder(suborderId);

      notify(
        translate(
          'pos.orders.messages.suborderDeleted',
          'Suborder deleted successfully'
        ),
        { type: 'success' }
      );

      // Remove from local state
      setSuborders((prev) => prev.filter((s) => s.id !== suborderId));

      // Refresh the current order to get updated data
      const updatedOrder = await ordersService.getOrder(currentOrder.id);
      setCurrentOrder(updatedOrder);
    } catch (error: any) {
      console.error('Error deleting suborder:', error);
      notify(
        error?.message ||
          translate(
            'pos.orders.messages.errorDeletingSuborder',
            'Failed to delete suborder'
          ),
        { type: 'error' }
      );
    }
  };

  return (
    <FormProvider {...form}>
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/orders')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {translate('pos.common.action.back')}
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">
                  {isSuborder
                    ? translate(
                        'pos.orders.editSuborder',
                        'Edit Sub Order'
                      )
                    : translate('pos.orders.edit')}
                </h1>
                {currentOrder.parentOrderId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigate(`/orders/${currentOrder.parentOrderId}/edit`);
                    }}
                    className="h-8"
                  >
                    <ExternalLink className="h-3 w-3 mr-1.5" />
                    {translate(
                      'pos.orders.actions.viewParentOrder',
                      'View Parent Order'
                    )}
                  </Button>
                )}
              </div>
              <p className="text-gray-600">
                {translate('pos.orders.fields.reference')}:{' '}
                {currentOrder.reference}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <Badge className={`${getStatusColor(currentOrder.status)}`}>
              {translate(`pos.orders.status.${currentOrder.status}`)}
            </Badge>
            {currentOrder.paid && (
              <Badge
                variant="outline"
                className="text-green-600 border-green-600"
              >
                {translate('pos.orders.fields.paid')}
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-3 space-y-6">
            {/* Reference Display */}
            <ReferenceDisplay reference={currentOrder.reference} />

            {/* Order Type and Table Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.orderType')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    {
                      id: 'dine_in',
                      name: translate('pos.orders.orderTypes.dineIn'),
                    },
                    {
                      id: 'takeaway',
                      name: translate(
                        'pos.orders.orderTypes.takeaway'
                      ),
                    },
                    {
                      id: 'delivery',
                      name: translate(
                        'pos.orders.orderTypes.delivery'
                      ),
                    },
                  ].map((type) => (
                    <Button
                      key={type.id}
                      variant={orderType === type.id ? 'default' : 'outline'}
                      className="h-12"
                      disabled={!canEditOrder && !isPlatformDeliveryOrder}
                      onClick={() =>
                        form.setValue('orderType', type.id as any, {
                          shouldDirty: true,
                        })
                      }
                    >
                      {type.name}
                    </Button>
                  ))}
                </div>

                {isDineIn && (
                  <ModernTableSelector
                    disabled={!canEditOrder && !isPlatformDeliveryOrder}
                    currentOrderId={currentOrder.id}
                  />
                )}
              </CardContent>
            </Card>

            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.customer')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isFinished && hasCustomer && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mb-4">
                    <p className="text-sm text-blue-800">
                      {translate(
                        'pos.orders.messages.customerLocked',
                        'This order already has a customer and cannot be changed.'
                      )}
                    </p>
                    <div className="text-xs text-blue-600 mt-1">
                      <CustomerReferenceField
                        customerId={currentOrder.customerId}
                        showPoints={true}
                        showZalo={true}
                        sourcePlatform={(currentOrder as any).sourcePlatform}
                      />
                    </div>
                  </div>
                )}
                {isFinished && !hasCustomer && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
                    <p className="text-sm text-yellow-800">
                      {translate(
                        'pos.orders.messages.tagCustomerForMetrics',
                        'Tag a customer to enable customer metrics tracking for this order.'
                      )}
                    </p>
                  </div>
                )}
                {isStaff && (
                  <ModernCustomerSelector disabled={!canChangeCustomer} />
                )}
                {!isStaff && (
                  <div className="p-3 bg-gray-100 rounded-md">
                    <p className="text-sm text-gray-600">
                      {currentOrder.customerId
                        ? `Customer ID: ${currentOrder.customerId}`
                        : translate('pos.orders.messages.noCustomer')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Items Selection */}
            {isSuborder && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <p className="text-sm text-blue-800 font-medium">
                  {translate(
                    'pos.orders.messages.suborderEditRestriction',
                    'This is a suborder. You can modify items, but other fields (customer, table, promotions, date) cannot be changed.'
                  )}
                </p>
              </div>
            )}
            <ModernItemsGrid
              disabled={!canEditOrder && !isPlatformDeliveryOrder && !canAddItemsToSuborder}
            />
            {!canEditOrder && !isPlatformDeliveryOrder && (
              <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                <p className="text-gray-600">
                  {currentOrder.status === 'delivered'
                    ? translate(
                        'pos.orders.messages.orderDeliveredCannotBeModified'
                      )
                    : currentOrder.status === 'cancelled'
                    ? translate(
                        'pos.orders.messages.orderCancelledCannotBeModified'
                      )
                    : currentOrder.status === 'ready'
                    ? translate(
                        'pos.orders.messages.orderReadyCannotBeModified',
                        'Orders in "ready" status cannot be modified. Create a suborder to add more items.'
                      )
                    : currentOrder.paid
                    ? translate(
                        'pos.orders.messages.orderPaidCannotBeModified',
                        'Paid orders cannot be modified.'
                      )
                    : translate(
                        'pos.orders.messages.orderCannotBeModified'
                      )}
                </p>
                {/* Create Suborder button - only show for ready orders without parent (not suborders) */}
                {currentOrder.status === 'ready' && !currentOrder.parentOrderId && (
                  <Button
                    className="w-full sm:w-auto"
                    variant="default"
                    size="lg"
                    onClick={async () => {
                      try {
                        setLoadingSuborders(true);
                        const suborder = await ordersService.createSuborder(
                          currentOrder.id.toString()
                        );
                        notify(
                          translate(
                            'pos.orders.messages.suborderCreated',
                            'Suborder created successfully'
                          ),
                          { type: 'success' }
                        );
                        // Navigate to the new suborder
                        navigate(`/orders/${suborder.id}/edit`);
                      } catch (error: any) {
                        console.error('Error creating suborder:', error);
                        notify(
                          error?.message ||
                            translate(
                              'pos.orders.messages.suborderCreateFailed',
                              'Failed to create suborder'
                            ),
                          { type: 'error' }
                        );
                      } finally {
                        setLoadingSuborders(false);
                      }
                    }}
                    disabled={loadingSuborders}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {translate(
                      'pos.orders.actions.createSuborder',
                      'Create Suborder'
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Suborders Section - show for all parent orders */}
            {!currentOrder.parentOrderId && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {translate(
                        'pos.orders.fields.suborders',
                        'Suborders'
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingSuborders ? (
                      <p className="text-gray-500">
                        {translate('pos.common.message.loading', 'Loading...')}
                      </p>
                    ) : suborders.length === 0 ? (
                      <p className="text-gray-500">
                        {translate(
                          'pos.orders.messages.noSuborders',
                          'No suborders yet'
                        )}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {suborders.map((suborder) => {
                          const isExpanded = expandedSuborders.has(suborder.id);
                          const suborderTotal = (suborder.items || []).reduce(
                            (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
                            0
                          );

                          return (
                            <div
                              key={suborder.id}
                              className="border rounded-lg overflow-hidden"
                            >
                              {/* Suborder Header */}
                              <div
                                className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleSuborderExpansion(suborder.id)}
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">{suborder.reference}</p>
                                      <Badge
                                        className={getStatusColor(suborder.status)}
                                      >
                                        {translate(
                                          `pos.orders.tabs.${suborder.status}`,
                                          suborder.status
                                        )}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                      {suborder.items?.length || 0}{' '}
                                      {translate(
                                        'pos.orders.fields.items',
                                        'items'
                                      )}
                                      {' • '}
                                      <span data-sensitive-field="amount">
                                        {formatCurrency(suborderTotal)}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  {(suborder.items || []).length === 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSuborder(suborder.id);
                                      }}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/orders/${suborder.id}/edit`);
                                    }}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>

                              {/* Expanded Items List */}
                              {isExpanded && suborder.items && suborder.items.length > 0 && (
                                <div className="border-t bg-gray-50 p-3 space-y-2">
                                  {suborder.items.map((item, index) => (
                                    <div
                                      key={index}
                                      className="flex items-start justify-between p-2 bg-white rounded border"
                                    >
                                      <div className="flex-1 min-w-0">
                                        <SuborderProductName productId={item.productId} />
                                        {item.note && (
                                          <p className="text-xs text-gray-500 mt-1 italic">
                                            {item.note}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right ml-3">
                                        <p className="text-sm font-medium">
                                          {item.quantity} ×{' '}
                                          <span data-sensitive-field="amount">
                                            {formatCurrency(item.price || 0)}
                                          </span>
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          <span data-sensitive-field="amount">
                                            {formatCurrency(
                                              (item.price || 0) * (item.quantity || 0)
                                            )}
                                          </span>
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                  <div className="flex justify-between items-center pt-2 border-t mt-2">
                                    <span className="font-medium text-sm">
                                      {translate('pos.orders.fields.total', 'Total')}:
                                    </span>
                                    <span className="font-bold text-sm">
                                      <span data-sensitive-field="amount">
                                        {formatCurrency(suborderTotal)}
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 order-first lg:order-last">
            {/* Order Total - Sticky */}
            <div className="lg:sticky lg:top-4 z-10">
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {translate('pos.orders.fields.total')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <OrderTotalField />
                  
                  {/* Detailed Total Breakdown for Parent Orders */}
                  {!currentOrder.parentOrderId && suborders.length > 0 && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="text-sm font-medium text-muted-foreground mb-2">
                        {translate(
                          'pos.orders.fields.totalBreakdown',
                          'Total Breakdown'
                        )}
                      </div>
                      
                      {/* Parent Order Total */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">
                          {translate(
                            'pos.orders.fields.parentOrder',
                            'Parent Order'
                          )}
                          {' ('}
                          {currentOrder.items?.length || 0}{' '}
                          {translate('pos.orders.fields.items', 'items')}
                          {')'}
                        </span>
                        <span className="font-medium" data-sensitive-field="amount">
                          {formatCurrency(
                            (currentOrder.items || []).reduce(
                              (sum, item) =>
                                sum + (item.price || 0) * (item.quantity || 0),
                              0
                            )
                          )}
                        </span>
                      </div>
                      
                      {/* Suborder Totals */}
                      {suborders.map((suborder) => {
                        const suborderTotal = (suborder.items || []).reduce(
                          (sum, item) =>
                            sum + (item.price || 0) * (item.quantity || 0),
                          0
                        );
                        return (
                          <div
                            key={suborder.id}
                            className="flex justify-between items-center text-sm pl-4 border-l-2 border-gray-200"
                          >
                            <span className="text-gray-600">
                              {suborder.reference}
                              {' ('}
                              {suborder.items?.length || 0}{' '}
                              {translate('pos.orders.fields.items', 'items')}
                              {')'}
                            </span>
                            <span className="font-medium" data-sensitive-field="amount">
                              {formatCurrency(suborderTotal)}
                            </span>
                          </div>
                        );
                      })}
                      
                      {/* Combined Total */}
                      <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-300">
                        <span className="font-semibold text-gray-900">
                          {translate(
                            'pos.orders.fields.combinedTotal',
                            'Combined Total'
                          )}
                        </span>
                        <span className="text-lg font-bold text-green-600" data-sensitive-field="amount">
                          {formatCurrency(
                            (currentOrder.items || []).reduce(
                              (sum, item) =>
                                sum + (item.price || 0) * (item.quantity || 0),
                              0
                            ) +
                              suborders.reduce(
                                (sum, suborder) =>
                                  sum +
                                  (suborder.items || []).reduce(
                                    (itemSum, item) =>
                                      itemSum +
                                      (item.price || 0) * (item.quantity || 0),
                                    0
                                  ),
                                0
                              )
                          )}
                        </span>
                      </div>
                      
                      {/* Total Item Count */}
                      <div className="flex justify-between items-center text-xs text-gray-500 pt-1">
                        <span>
                          {translate(
                            'pos.orders.fields.totalItems',
                            'Total Items'
                          )}
                        </span>
                        <span>
                          {(currentOrder.items?.length || 0) +
                            suborders.reduce(
                              (sum, suborder) =>
                                sum + (suborder.items?.length || 0),
                              0
                            )}{' '}
                          {translate('pos.orders.fields.items', 'items')}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Show promotion status if promotions exist */}
                  {currentOrder.promotionIds &&
                    currentOrder.promotionIds.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <label className="text-sm font-medium text-muted-foreground mb-2 block">
                          {translate(
                            'pos.orders.fields.promotionStatus',
                            'Promotion Status'
                          )}
                        </label>
                        <PromotionStatusBadge
                          promotionIds={currentOrder.promotionIds}
                        />
                      </div>
                    )}
                </CardContent>
              </Card>
            </div>
            {/* Promotion Section */}
            <PromotionSection disabled={!canEditPromotion} />

            {/* Date Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.date')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="datetime-local"
                  className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={formatDateForDateTimeLocal(form.watch('date'))}
                  onChange={(e) =>
                    form.setValue('date', new Date(e.target.value), {
                      shouldDirty: true,
                    })
                  }
                  disabled={!canEditDate}
                />
                {isPlatformDeliveryOrder && (
                  <p className="text-sm text-gray-500 mt-2">
                    {translate(
                      'pos.orders.messages.platformDeliveryDateEditable',
                      'Date can be adjusted for platform delivery orders imported later'
                    )}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <Button
                    className="w-full"
                    size="lg"
                    disabled={
                      !canSave && !canSaveCustomerTag && !canSavePlatformOrder && !canSaveSuborder
                    }
                    onClick={form.handleSubmit(async (data) => {
                      try {
                        if (canTagCustomer && data.tempCustomerId) {
                          // Update only customerId for finished orders without customer
                          console.log('Tagging customer to finished order:', {
                            orderId: currentOrder.id,
                            customerId: data.tempCustomerId,
                          });
                          await ordersService.updateOrder(currentOrder.id, {
                            customerId: data.tempCustomerId,
                          });
                          // Refresh order data
                          const updatedOrder = (await ordersService.getOrder(
                            currentOrder.id
                          )) as Order;
                          setCurrentOrder(updatedOrder);

                          // Reset form to clear dirty state after successful save
                          form.reset({
                            reference: updatedOrder.reference,
                            promotionIds: updatedOrder.promotionIds || [],
                            promotionType: 'none',
                            promotionValue: 0,
                            customerPoints: null,
                            pointsRedemption:
                              updatedOrder.pointsRedemption || 0,
                            orderType: updatedOrder.orderType as
                              | 'dine_in'
                              | 'takeaway'
                              | 'delivery',
                            tableNumber:
                              typeof updatedOrder.tableNumber === 'number'
                                ? updatedOrder.tableNumber
                                : updatedOrder.tableNumber
                                ? Number(updatedOrder.tableNumber)
                                : undefined,
                            items: (updatedOrder.items || []).map((item) => ({
                              productId: String(item.productId),
                              quantity: item.quantity,
                              price: item.price,
                              note: item.note,
                            })),
                            date: new Date(updatedOrder.date),
                            status: updatedOrder.status,
                            customerId: String(updatedOrder.customerId || ''),
                            hasCheckedIn: false,
                            tempCustomerId: updatedOrder.customerId
                              ? String(updatedOrder.customerId)
                              : undefined,
                          });

                          if (onOrderUpdate) {
                            onOrderUpdate(updatedOrder);
                          }
                          // Show success message
                          notify(
                            translate(
                              'pos.orders.messages.customerTagged',
                              'Customer tagged successfully'
                            ),
                            { type: 'success' }
                          );
                        } else if (canSave || canSavePlatformOrder || canSaveSuborder) {
                          // Regular order update (including platform delivery orders and suborders)
                          console.log('Updating order:', data);

                          // For suborders, only send newly added items (not modifications to existing items)
                          let itemsToSend = data.items;
                          if (isSuborder && originalItemsCount !== undefined) {
                            // Only send items that were added (after originalItemsCount)
                            // Keep original items unchanged
                            const originalItems = data.items.slice(0, originalItemsCount);
                            const newItems = data.items.slice(originalItemsCount);
                            // Combine original items (unchanged) with new items
                            itemsToSend = [...originalItems, ...newItems];
                            console.log('Suborder update - original items:', originalItems.length, 'new items:', newItems.length);
                          }

                          // Transform form data to API format
                          // Backend will handle promotion lookup/creation and total calculation
                          const hasSelectedPromotions =
                            (data.promotionIds || []).length > 0;
                          const promotionValue = data.promotionValue || 0;
                          const hasManualPromotion =
                            data.promotionType &&
                            data.promotionType !== 'none' &&
                            promotionValue > 0;

                          const orderData: any = {
                            reference: data.reference,
                            orderType: data.orderType,
                            tableNumber: data.tableNumber,
                            items: itemsToSend,
                            date: data.date,
                            customerId:
                              data.customerId ||
                              data.tempCustomerId ||
                              undefined,
                            status: data.status,
                            pointsRedemption: data.pointsRedemption || 0,
                          };
                          
                          // For suborders, prevent modifying other fields (but allow item modifications)
                          if (isSuborder) {
                            // Preserve all non-item fields from original order
                            orderData.reference = currentOrder.reference;
                            orderData.orderType = currentOrder.orderType;
                            orderData.tableNumber = currentOrder.tableNumber;
                            orderData.date = currentOrder.date;
                            orderData.customerId = currentOrder.customerId;
                            orderData.status = currentOrder.status;
                            orderData.pointsRedemption = currentOrder.pointsRedemption || 0;
                            orderData.promotionIds = currentOrder.promotionIds || [];
                            // Items can be modified (added, removed, quantity changed, notes changed)
                          }

                          // Send promotionIds if selected, otherwise send manual promotion fields for backward compatibility
                          if (hasSelectedPromotions) {
                            orderData.promotionIds = data.promotionIds || [];
                          } else if (hasManualPromotion && data.promotionType) {
                            // Backend will create temporary campaign from these fields
                            orderData.promotionType = data.promotionType;
                            orderData.promotionValue = promotionValue;
                            orderData.promotionIds = []; // Empty array, backend will create new promotion
                          } else {
                            orderData.promotionIds = [];
                          }

                          console.log(
                            '🚀 Updating order (backend will calculate totals):',
                            {
                              ...orderData,
                              promotionType: data.promotionType,
                              promotionValue: promotionValue,
                              hasPromotion: hasManualPromotion,
                            }
                          );
                          await ordersService.updateOrder(
                            currentOrder.id,
                            orderData
                          );
                          const updatedOrder = (await ordersService.getOrder(
                            currentOrder.id
                          )) as Order;
                          setCurrentOrder(updatedOrder);
                          
                          // For suborders, update originalItemsCount after save
                          // This makes newly added items "original" so they can't be edited in future edits
                          if (isSuborder) {
                            setOriginalItemsCount(updatedOrder.items?.length || 0);
                          }

                          // Reset form to clear dirty state after successful save
                          form.reset({
                            reference: updatedOrder.reference,
                            promotionIds: updatedOrder.promotionIds || [],
                            promotionType: 'none',
                            promotionValue: 0,
                            customerPoints: null,
                            pointsRedemption:
                              updatedOrder.pointsRedemption || 0,
                            orderType: updatedOrder.orderType as
                              | 'dine_in'
                              | 'takeaway'
                              | 'delivery',
                            tableNumber:
                              typeof updatedOrder.tableNumber === 'number'
                                ? updatedOrder.tableNumber
                                : updatedOrder.tableNumber
                                ? Number(updatedOrder.tableNumber)
                                : undefined,
                            items: (updatedOrder.items || []).map((item) => ({
                              productId: String(item.productId),
                              quantity: item.quantity,
                              price: item.price,
                              note: item.note,
                            })),
                            date: new Date(updatedOrder.date),
                            status: updatedOrder.status,
                            customerId: String(updatedOrder.customerId || ''),
                            hasCheckedIn: false,
                            tempCustomerId: updatedOrder.customerId
                              ? String(updatedOrder.customerId)
                              : undefined,
                          });

                          // Update table status if table number changed
                          if (orderData.tableNumber !== undefined) {
                            const { occupyTable, releaseTable } =
                              useTableStatusStore.getState();
                            const previousTableNumber =
                              currentOrder.tableNumber;
                            const newTableNumber = orderData.tableNumber;

                            try {
                              // Release previous table if it changed
                              if (
                                previousTableNumber &&
                                previousTableNumber !== newTableNumber
                              ) {
                                await releaseTable(previousTableNumber);
                              }

                              // Occupy new table if provided
                              if (newTableNumber) {
                                await occupyTable(
                                  newTableNumber,
                                  currentOrder.id
                                );
                              } else if (previousTableNumber) {
                                // Table was removed
                                await releaseTable(previousTableNumber);
                              }
                            } catch (error) {
                              console.error(
                                'Failed to update table status:',
                                error
                              );
                              // Don't fail the order update if table status update fails
                            }

                            await fetchTableStatuses();
                          }

                          if (onOrderUpdate) {
                            onOrderUpdate(updatedOrder);
                          }
                          notify(
                            translate(
                              'pos.common.message.updated',
                              'Order updated successfully'
                            ),
                            { type: 'success' }
                          );
                        }
                      } catch (error) {
                        console.error('Error updating order:', error);
                        notify(
                          translate(
                            'pos.common.message.error',
                            'Failed to update order'
                          ),
                          { type: 'error' }
                        );
                      }
                    })}
                  >
                    {canTagCustomer
                      ? translate(
                          'pos.orders.actions.tagCustomer',
                          'Tag Customer'
                        )
                      : translate('pos.common.action.save')}
                  </Button>

                  {currentOrder.status !== 'cancelled' && (
                    <PrintBillButton orderId={currentOrder.id.toString()} />
                  )}

                  <CancelOrderButton
                    orderId={currentOrder.id.toString()}
                    orderStatus={currentOrder.status}
                    orderPaid={currentOrder.paid}
                    onCancelSuccess={async () => {
                      // Refresh the order after cancellation
                      try {
                        const updatedOrder = await ordersService.getOrder(
                          currentOrder.id.toString()
                        );
                        setCurrentOrder(updatedOrder);
                      } catch (error) {
                        console.error('Error refreshing order:', error);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export const ModernOrderEdit = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);
        const orderData = (await ordersService.getOrder(id)) as Order;
        setOrder(orderData);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch order');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Error loading order
          </h2>
          <p className="text-gray-600">
            The order could not be found or loaded.
          </p>
        </div>
      </div>
    );
  }

  return <OrderForm order={order} />;
};

export default ModernOrderEdit;
